/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import Retracked from 'js/app/retracked';
import API from 'js/lib/api';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import type { ClickStatus } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication/constants';
import ForgotPasswordForm from 'bundles/authentication/shared/ForgotPasswordForm';
import LinkButton from 'bundles/authentication/shared/components/authentication-modal/LinkButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/authentication';

export type Props = {
  className?: string;
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  magicLinkRedirect?: string;
  hideBackToLink?: boolean;
};

export type State = {
  isSent: boolean;
  requestMagicLink: boolean;
};

const api = API('', { type: 'rest' });

const styles = {
  container: css`
    display: grid;
    grid-gap: 24px;
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: 32px 16px;
  `,
  description: css`
    text-align: center;
  `,
  navigation: css`
    margin: 12px auto 0;
    text-align: center;
    display: flex;
  `,
  navigationSent: css`
    margin-top: 150px;
  `,
  navigationIcon: css`
    margin-right: 0.6rem;
  `,
  section: css`
    &:nth-child(1) {
      -ms-grid-row: 1;
    }

    &:nth-child(2) {
      -ms-grid-row: 2;
    }

    &:nth-child(3) {
      -ms-grid-row: 3;
    }

    &:nth-child(4) {
      -ms-grid-row: 4;
    }
  `,
  title: css`
    color: #1f1f1f;
    font-size: 24px;
    line-height: 30px;
    margin: 0;
    text-align: center;

    @media (min-width: 600px) {
      font-size: 34px;
      line-height: 42px;
    }
  `,
};

const ForgotPassword = (props: Props) => {
  const { className, hideBackToLink = false, navigate, magicLinkRedirect } = props;
  const [isSent, setIsSent] = useState(false);
  const [requestMagicLink, setRequestMagicLink] = useState(false);
  const trackEvent = useTracker();

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_modal', {
    modal: {
      name: 'reset_password_email_sent',
    },
  });

  const handleTracking = (status: ClickStatus) => {
    trackEvent('click_button', {
      button: {
        name: 'reset_password_email_request_form_submit',
        clickStatus: status,
      },
      modal: { name: 'forgot_password' },
    });
  };

  const navigateToLogin = () => {
    trackEvent('click_button', {
      button: { name: 'back_to_login_form' },
      modal: { name: 'forgot_password' },
    });
    navigate(AUTHENTICATION_MODAL_MODES.LOGIN);
  };

  const navigateToMagicLink = (email: string) => {
    navigate(AUTHENTICATION_MODAL_MODES.MAGICLINK, { email });
  };

  const resetPassword = ({ email }: { email: string }) => {
    if (!email) {
      handleTracking('invalid');
      return;
    }

    handleTracking('valid');
    api.post('api/password/v1/requestReset', { data: { email } });
    setIsSent(true);
  };

  const sendMagicLink = ({ email }: { email: string }) => {
    trackEvent('click_button', {
      button: { name: 'email_me_login_link' },
      modal: { name: 'forgot_password' },
    });
    api.post('/api/magicLinks.v1/encode', { data: { email, redirectUrl: magicLinkRedirect } });
    navigateToMagicLink(email);
  };

  const requestMagicLinkHandler = () => {
    setRequestMagicLink(true);
  };

  return (
    <section ref={isSent ? visibilityTrackingRef : null} css={[styles.container, className]} aria-live="polite">
      <header css={styles.section}>
        <h1 css={styles.title}>{isSent ? _t('Email has been sent') : _t('Forgot password')}</h1>
      </header>
      <p css={[styles.description, styles.section]}>
        {isSent ? (
          <TrackedDiv trackingName="reset_request_sent" withVisibilityTracking={true}>
            {_t("Please check your email inbox for a password recovery link. Don't forget to check your spam folder.")}
          </TrackedDiv>
        ) : (
          _t("Enter the email address you use on Coursera. We'll send you a link to reset your password.")
        )}
      </p>
      {!isSent && (
        <ForgotPasswordForm
          onSubmit={requestMagicLink ? sendMagicLink : resetPassword}
          onRequestMagicLink={requestMagicLinkHandler}
          submitCTALabel={_t('Reset Password')}
          submitCTATrackingName="reset_page_button"
        />
      )}
      {!hideBackToLink && (
        <p css={[styles.navigation, isSent && styles.navigationSent, styles.section]}>
          <FormattedMessage
            message={_t('Back to {loginLink}')}
            loginLink={<LinkButton onClick={navigateToLogin}>{_t('Log in')}</LinkButton>}
          />
        </p>
      )}
    </section>
  );
};

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'reset_password_page',
  },
}))(ForgotPassword);
